import { EntityStatus, TDocumentType } from '@verifime/utils';

export type TSupportDocumentSubType =
  | 'WholesaleCertificate'
  | 'CertificateOfTitle'
  | 'ValuationNotice'
  | 'RatesNotice'
  | 'NationalVendorDeclarationForLivestock'
  | 'Other';

// WTF do we need to define Empty EntityStatus?
type TEntityStatus = Exclude<keyof typeof EntityStatus, 'Empty'>;

type TSupportDocumentType = Extract<
  TDocumentType,
  'RealEstateSupportDocument' | 'WholesaleCertificate' | 'OtherSupportDocument'
>;

export const SupportDocumentTypeOptions: {
  [subtype in TSupportDocumentSubType]: { type: string; description: string };
} = {
  ['WholesaleCertificate']: {
    type: 'Wholesale Certificate',
    description: 'Invest in financial products as a certified wholesale investor',
  },
  ['CertificateOfTitle']: {
    type: 'Certificate of Title',
    description: 'Prove ownership of your property',
  },
  ['ValuationNotice']: {
    type: 'Valuation Notice',
    description: 'Prove ownership of your property',
  },
  ['RatesNotice']: {
    type: 'Rates Notice',
    description: 'Prove ownership of your property',
  },
  ['NationalVendorDeclarationForLivestock']: {
    type: 'National Vendor Declaration for Livestock',
    description: '',
  },
  ['Other']: {
    type: 'Other Documents',
    description: '',
  },
};

export const IndustryDetails: Record<string, { title: string; description: string }> = {
  ['WholesaleCertificate']: {
    title: 'Investment',
    description:
      'Share documents like wholesale investor certificates and more securely with your fund manager.',
  },
  ['RealEstateSupportDocument']: {
    title: 'Real Estate',
    description: 'Share documents like property titles, and more securely with your provider.',
  },
  ['OtherSupportDocument']: {
    title: 'Other Documents',
    description: '',
  },
};

export function toDocumentType(
  supportDocumentSubType?: TSupportDocumentSubType,
): TSupportDocumentType {
  switch (supportDocumentSubType) {
    case 'CertificateOfTitle':
    case 'RatesNotice':
    case 'ValuationNotice':
    case 'NationalVendorDeclarationForLivestock':
      return 'RealEstateSupportDocument';
    case 'WholesaleCertificate':
      return 'WholesaleCertificate';
    case 'Other':
      return 'OtherSupportDocument';
    default:
      throw Error('Unsupported document sub type');
  }
}

type TSpecificSupportDocumentAttributes =
  | TWholesaleCertificate
  | TCertificateOfTitle
  | TValuationNotice
  | TRatesNotice
  | TNationalVendorDeclarationForLivestock
  | TOtherSupportDocument;

export type TSupportDocumentBaseAttributes = {
  id?: string;
  status?: TEntityStatus;
  documentType?: TSupportDocumentType;
  supportDocumentSubType?: TSupportDocumentSubType;
  keyAttributeName?: string;
};

export type TSupportDocument = TSupportDocumentBaseAttributes & TSpecificSupportDocumentAttributes;

function isSupportDocumentDataValid(data: any): boolean {
  if (!data.status || !data.supportDocumentSubType) {
    return false;
  }

  if (data.status === 'Verified') {
    const attributeMapping = getSupportDocumentAttributeMapping(data.supportDocumentSubType);

    return Object.entries(attributeMapping).every(([key, value]) => {
      if (value.optional) {
        return true;
      }
      return data[key] !== undefined;
    });
  }

  return true;
}

type GroupedDocuments = {
  [key in TDocumentType]: {
    [supportDocumentSubType: string]: TSupportDocument[];
  };
};

export function groupByDocumentTypeAndSubType(data: any[]) {
  return data.reduce((acc: GroupedDocuments, item: TSupportDocument) => {
    if (isSupportDocumentDataValid(item)) {
      let supportDocumentSubType = item.supportDocumentSubType;
      let documentType = item.documentType as TDocumentType;

      if (!documentType) {
        documentType = toDocumentType(supportDocumentSubType);
      }

      if (!acc[documentType]) {
        acc[documentType] = {};
      }

      if (supportDocumentSubType) {
        if (!acc[documentType][supportDocumentSubType]) {
          acc[documentType][supportDocumentSubType] = [];
        }

        acc[documentType][supportDocumentSubType].push(item);
      }
    }
    return acc;
  }, {} as GroupedDocuments);
}

enum FormatType {
  Date = 'Date',
  Array = 'Array',
  Money = 'Money',
  TextArea = 'TextArea',
}

export interface AttributeMapping {
  label: string;
  formatType?: FormatType;
  optional?: boolean;
}

export type TWholesaleCertificate = {
  dateIssued?: string;
  dateOfExpiry?: string;
  accountantNumber?: string;
};

export const wholesaleCertificateAttributesMapping: {
  [key in keyof TWholesaleCertificate]: AttributeMapping;
} = {
  dateIssued: { label: 'Date of Issue', formatType: FormatType.Date },
  dateOfExpiry: { label: 'Date of Expiry', formatType: FormatType.Date },
  accountantNumber: { label: 'Accountant Number' },
};

export type TCertificateOfTitle = {
  dateOfIssue?: string;
  propertyOwners?: string[];
  volume?: string;
  folio?: string;
  descriptionOfLand?: string;
};

export const certificateOfTitleAttributesMapping: {
  [key in keyof TCertificateOfTitle]: AttributeMapping;
} = {
  dateOfIssue: { label: 'Date of Issue', formatType: FormatType.Date },
  propertyOwners: { label: 'Property Owner(s)', formatType: FormatType.Array },
  volume: { label: 'Volume' },
  folio: { label: 'Folio' },
  descriptionOfLand: { label: 'Description of Land', formatType: FormatType.TextArea },
};

export type TValuationNotice = {
  landValueAsAt?: string;
  landValue?: number;
  ownersOrLessees?: string[];
  addressOfProperty?: string;
  descriptionOfLand?: string;
};

export const valuationNoticeAttributeMapping: {
  [key in keyof TValuationNotice]: AttributeMapping;
} = {
  landValueAsAt: { label: 'Land value as at', formatType: FormatType.Date },
  landValue: { label: 'Land value', formatType: FormatType.Money },
  ownersOrLessees: { label: 'Owner(s) or Lessee(s)', formatType: FormatType.Array },
  addressOfProperty: { label: 'Address of Property' },
  descriptionOfLand: { label: 'Description of Land', formatType: FormatType.TextArea },
};

export type TRatesNotice = {
  dateOfIssue?: string;
  addressOfProperty?: string;
  propertyOwners?: string[];
  valuationDate?: string;
  valuation?: number;
  descriptionOfLand?: string;
};

export const ratesNoticeAttributeMapping: { [key in keyof TRatesNotice]: AttributeMapping } = {
  dateOfIssue: { label: 'Date of Issue', formatType: FormatType.Date },
  addressOfProperty: { label: 'Address of Property' },
  propertyOwners: { label: 'Property Owner(s)', formatType: FormatType.Array },
  valuationDate: { label: 'Valuation Date', formatType: FormatType.Date, optional: true },
  valuation: { label: 'Valuation', formatType: FormatType.Money, optional: true },
  descriptionOfLand: { label: 'Description of Land', formatType: FormatType.TextArea },
};

export type TNationalVendorDeclarationForLivestock = {
  declarationDate?: string;
  propertyIdentificationCode?: string;
};

export const nationalVendorDeclarationForLivestockAttributeMapping: {
  [key in keyof TNationalVendorDeclarationForLivestock]: AttributeMapping;
} = {
  declarationDate: { label: 'Declaration Date', formatType: FormatType.Date },
  propertyIdentificationCode: { label: 'Property Identification Code' },
};

export type TOtherSupportDocument = {
  otherSupportDocumentType?: string;
  comment?: string;
};

export const otherSupportDocumentAttributeMapping: {
  [key in keyof TOtherSupportDocument]: AttributeMapping;
} = {
  otherSupportDocumentType: { label: 'Document Type' },
  comment: { label: 'Comment' },
};

function createInitialData(attributeMapping: { [key: string]: AttributeMapping }): {
  [key: string]: any;
} {
  const initialData: { [key: string]: any } = {};
  Object.keys(attributeMapping).forEach((key) => {
    initialData[key] = null;
  });
  return initialData;
}

export function getSupportDocumentAttributeMapping(subType: string): {
  [key: string]: AttributeMapping;
} {
  switch (subType) {
    case 'CertificateOfTitle':
      return certificateOfTitleAttributesMapping;
    case 'ValuationNotice':
      return valuationNoticeAttributeMapping;
    case 'RatesNotice':
      return ratesNoticeAttributeMapping;
    case 'NationalVendorDeclarationForLivestock':
      return nationalVendorDeclarationForLivestockAttributeMapping;
    case 'Other':
      return otherSupportDocumentAttributeMapping;
    case 'WholesaleCertificate':
      return wholesaleCertificateAttributesMapping;
    default:
      throw new Error('Unsupported document subtype');
  }
}

export function getInitialDataBySubType(subType: TSupportDocumentSubType): { [key: string]: any } {
  switch (subType) {
    case 'CertificateOfTitle':
      return createInitialData(certificateOfTitleAttributesMapping);
    case 'ValuationNotice':
      return createInitialData(valuationNoticeAttributeMapping);
    case 'RatesNotice':
      return createInitialData(ratesNoticeAttributeMapping);
    case 'NationalVendorDeclarationForLivestock':
      return createInitialData(nationalVendorDeclarationForLivestockAttributeMapping);
    case 'Other':
      return createInitialData(otherSupportDocumentAttributeMapping);
    case 'WholesaleCertificate':
      return createInitialData(wholesaleCertificateAttributesMapping);
    default:
      throw new Error('Unsupported document subtype');
  }
}

type DocumentTypeMap = {
  ['WholesaleCertificate']: TSupportDocument & TWholesaleCertificate;
  ['CertificateOfTitle']: TSupportDocument & TCertificateOfTitle;
  ['ValuationNotice']: TSupportDocument & TValuationNotice;
  ['RatesNotice']: TSupportDocument & TRatesNotice;
  ['NationalVendorDeclarationForLivestock']: TSupportDocument &
    TNationalVendorDeclarationForLivestock;
  ['Other']: TSupportDocument & TOtherSupportDocument;
};

export function isSupportDocumentSubType<T extends TSupportDocumentSubType>(
  document: TSupportDocument,
  type: T,
): document is DocumentTypeMap[T] {
  if (document.supportDocumentSubType !== type) return false;

  const attributeMapping = getSupportDocumentAttributeMapping(type);
  return Object.keys(attributeMapping).some((key) => (document as any)[key] !== undefined);
}
